/**
 * ProgressionTowerWidget - Уровень прокачки башен по номеру кампании
 * ===================================================================
 *
 * Два графика в одном виджете (один запрос): «обычные» башни и «ивентовые».
 * По одной кривой на башню (tower_name). По оси X — номер кампании
 * (acc_progress). По Y — текущий уровень башни с forward-fill: на игрока берём
 * накопительный максимум и протягиваем его по кампаниям до макс. кампании
 * игрока, затем avg/median между игроками.
 * Данные из ClickHouse (gamegears.prod_events_hole_alien), когорта по acc_reg_ts.
 * Общий переключатель avg / median; отдельные башни включаются/выключаются
 * кликом по легенде recharts (состояние скрытия — у каждого графика своё).
 */

import React, { useState } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { withDefaultDateRange } from '../../utils/defaultDateRange';
import { completedDaysSubtitle } from '../../utils/completedDaysWindow';
import { ToggleGroup, ToggleGroupItem } from '../ui/toggle-group';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { progression as progressionSpecs } from '@ggstat/chart-specs';

// Палитра кривых башен и подписи групп — общие с MCP (shared/chart-specs)
const TOWER_COLORS = progressionSpecs.towerByCampaign.palette;
const [REGULAR_GROUP, EVENT_GROUP] = progressionSpecs.towerByCampaign.groups;

// 'fire_mortar' -> 'Fire Mortar'
function prettyTower(name) {
  return String(name)
    .split('_')
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : p))
    .join(' ');
}

const colorOf = (i) => TOWER_COLORS[i % TOWER_COLORS.length];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const sorted = payload;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3">
      <p className="font-medium mb-2">Кампания №{label}</p>
      {sorted.map((entry) => (
        <p key={entry.name} className="text-sm" style={{ color: entry.color }}>
          {entry.name}: {Number(entry.value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
        </p>
      ))}
    </div>
  );
};

/**
 * Одна секция-график (обычные ИЛИ ивентовые башни). Держит собственное
 * состояние скрытия кривых по клику в легенде.
 */
function TowerChartSection({ title, towers, points, suffix }) {
  const [hidden, setHidden] = useState({});

  const handleLegendClick = (entry) => {
    // dataKey вида '<tower>Avg' | '<tower>Median' -> имя башни
    const tower = String(entry.dataKey || '').replace(/(Avg|Median)$/, '');
    if (!tower) return;
    setHidden((prev) => ({ ...prev, [tower]: !prev[tower] }));
  };

  return (
    <div>
      <h4 className="text-sm font-medium text-muted-foreground mb-2">{title}</h4>
      {towers.length === 0 ? (
        <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
          Нет данных за выбранную когорту
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={460}>
          <LineChart data={points} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="campaignNumber"
              type="number"
              domain={['dataMin', 'dataMax']}
              allowDecimals={false}
              tick={{ fill: 'currentColor' }}
              className="text-xs"
              label={{ value: 'Номер кампании', position: 'insideBottom', offset: -2 }}
            />
            <YAxis
              tick={{ fill: 'currentColor' }}
              className="text-xs"
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend onClick={handleLegendClick} wrapperStyle={{ cursor: 'pointer' }} />
            {towers.map((tower, i) => (
              <Line
                key={tower}
                type="monotone"
                dataKey={`${tower}${suffix}`}
                name={prettyTower(tower)}
                stroke={colorOf(i)}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
                isAnimationActive={false}
                connectNulls
                hide={!!hidden[tower]}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export function ProgressionTowerWidget() {
  const [aggregation, setAggregation] = useState('avg');
  const suffix = aggregation === 'median' ? 'Median' : 'Avg';

  // Когорта обязательна: без диапазона дат сервер отвечает 400 (иначе запрос
  // читает все партиции таблицы событий). Пустой диапазон → последние 30 дней.
  const widgetData = useWidgetData(
    async (filters, options) =>
      MetricsService.getProgressionTowerByCampaign(withDefaultDateRange(filters), options)
  );

  const widgetProps = useBaseWidget(widgetData);
  const { data } = widgetProps;

  const regular = data?.regular || { towers: [], data: [] };
  const event = data?.event || { towers: [], data: [] };
  const dateRange = data?.dateRange;

  const Controls = () => (
    <ToggleGroup
      type="single"
      value={aggregation}
      onValueChange={(value) => value && setAggregation(value)}
    >
      <ToggleGroupItem value="avg" aria-label="Среднее">Среднее</ToggleGroupItem>
      <ToggleGroupItem value="median" aria-label="Медиана">Медиана</ToggleGroupItem>
    </ToggleGroup>
  );

  return (
    <BaseWidget
      {...widgetProps}
      title="Уровень башен по номеру кампании (когорта)"
      subtitle={completedDaysSubtitle('Когорта (регистрация)', dateRange)}
      headerAction={<Controls />}
      emptyMessage="Нет данных за выбранную когорту"
      skeletonRows={8}
    >
      <div className="space-y-8">
        <TowerChartSection
          title={REGULAR_GROUP.label}
          towers={regular.towers}
          points={regular.data}
          suffix={suffix}
        />
        <TowerChartSection
          title={EVENT_GROUP.label}
          towers={event.towers}
          points={event.data}
          suffix={suffix}
        />
      </div>
    </BaseWidget>
  );
}

export default ProgressionTowerWidget;
